import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Plus, Loader2, Search, X, Send, Trash2, Paperclip, MessageSquare, ListChecks,
  Ticket as TicketIcon, Lock, Unlock, AlertTriangle, CheckCircle2, Circle,
  Download, Upload, Filter, ChevronDown, Building2, Pencil, Bell, History,
  Folder, FolderPlus, FileText, Eye, Star, History as HistoryIcon,
  LayoutGrid, List as ListIcon, Settings, Clock,
} from 'lucide-react';
import {
  crmStats, listDepartments, createDepartment, updateDepartment, deleteDepartment,
  listTickets, createTicket, updateTicket, deleteTicket, followUpTicket,
  listTasks, createTask, updateTask, deleteTask,
  listMessages, createMessage, markMessagesRead, setTyping, getTyping,
  listCrmFiles, listCrmFilesTree, listFileVersions, createCrmFile, deleteCrmFile,
  listFileComments, createFileComment,
  listUsers, uploadFile, resolveFileUrl,
  runDigest, rateTicket, listTicketRatings,
  getNudgeSettings, putNudgeSettings, runNudgeNow,
} from '../../services/api';
import { toast } from '../../hooks/use-toast';
import { useAuth, isSuperAdmin } from '../../context/AuthContext';
import KanbanBoard from '../../components/crm/KanbanBoard';
import MultiSelect from '../../components/MultiSelect';
import DocsTab from '../../components/DocsTab';

const STATUS_META = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-700' },
  assigned: { label: 'Assigned', color: 'bg-indigo-100 text-indigo-700' },
  in_progress: { label: 'In progress', color: 'bg-amber-100 text-amber-700' },
  review: { label: 'Review', color: 'bg-purple-100 text-purple-700' },
  won: { label: 'Won 🏆', color: 'bg-[#0f5c3d] text-white' },
  closed: { label: 'Closed', color: 'bg-emerald-100 text-emerald-700' },
  reopened: { label: 'Reopened', color: 'bg-rose-100 text-rose-700' },
};
const PRIORITY_META = {
  low: { label: 'Low', color: 'text-slate-500' },
  medium: { label: 'Medium', color: 'text-blue-600' },
  high: { label: 'High', color: 'text-amber-600' },
  urgent: { label: 'Urgent', color: 'text-red-600' },
};
const TASK_STATUS = { todo: 'To do', in_progress: 'In progress', review: 'Review', snoozed: 'Paused', done: 'Done' };

const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const CRMPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [depts, setDepts] = useState([]);
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [filters, setFilters] = useState({ status: '', priority: '', department_id: '', q: '' });
  const [ticketView, setTicketView] = useState('list'); // 'list' | 'kanban'
  const [showNudgeSettings, setShowNudgeSettings] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showDeptMgr, setShowDeptMgr] = useState(false);

  const userLookup = useMemo(() => {
    const m = {};
    users.forEach((u) => { m[u.user_id] = u; });
    return m;
  }, [users]);
  const deptLookup = useMemo(() => {
    const m = {};
    depts.forEach((d) => { m[d.id] = d; });
    return m;
  }, [depts]);

  const reloadStats = useCallback(async () => {
    try { setStats(await crmStats()); } catch (err) { console.error('crmStats failed', err); }
  }, []);
  const reloadTickets = useCallback(async () => {
    setLoadingList(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.priority) params.priority = filters.priority;
      if (filters.department_id) params.department_id = filters.department_id;
      if (filters.q) params.q = filters.q;
      const r = await listTickets(params);
      setTickets(r.data || []);
    } catch (err) { console.error('listTickets failed', err); toast({ title: 'Failed to load tickets', variant: 'destructive' }); }
    finally { setLoadingList(false); }
  }, [filters]);

  useEffect(() => {
    listDepartments().then((r) => setDepts(r.data || [])).catch(() => {});
    listUsers().then((r) => setUsers(r.data || [])).catch(() => {});
    reloadStats();
  }, [reloadStats]);
  useEffect(() => { reloadTickets(); }, [reloadTickets]);

  const selected = tickets.find((t) => t.id === selectedId) || null;

  const onTicketCreated = async (t) => {
    setShowNew(false);
    await reloadTickets();
    await reloadStats();
    setSelectedId(t.id);
  };

  return (
    <div data-testid="admin-crm-page">
      {/* Header + stats */}
      <div className="flex items-end justify-between gap-3 flex-wrap mb-4 sm:mb-5">
        <div className="min-w-0">
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">CRM</span>
          <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
            Tickets, Tasks & Messages
          </h1>
          <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-[#0b1a2e]/60">Internal workspace for the Emerald team.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          {isSuperAdmin(user) && (
            <button
              onClick={async () => {
                if (!window.confirm('Send the daily digest to all eligible users right now?')) return;
                try {
                  const r = await runDigest();
                  toast({ title: 'Digest fired', description: `Sent: ${r.sent} · Skipped: ${r.skipped} · Errors: ${r.errors}` });
                } catch (err) {
                  toast({ title: 'Digest failed', variant: 'destructive', description: err?.response?.data?.detail || '' });
                }
              }}
              data-testid="run-digest-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#0f5c3d]/20 text-xs font-medium hover:bg-[#f7fbf8]"
              title="Force-send the daily digest to all users now"
            >
              <Send className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Send digest now</span><span className="sm:hidden">Digest</span>
            </button>
          )}
          <button onClick={() => setShowDeptMgr(true)} data-testid="manage-depts-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#0f5c3d]/20 text-xs font-medium hover:bg-[#f7fbf8]">
            <Building2 className="w-3.5 h-3.5" /> Departments
          </button>
          {isSuperAdmin(user) && (
            <button onClick={() => setShowNudgeSettings(true)} data-testid="nudge-settings-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#0f5c3d]/20 text-xs font-medium hover:bg-[#f7fbf8]">
              <Settings className="w-3.5 h-3.5" /> Nudge
            </button>
          )}
          <div className="flex gap-0.5 rounded-full bg-white border border-[#0f5c3d]/15 p-0.5 ml-auto sm:ml-0" data-testid="ticket-view-toggle">
            <button onClick={() => setTicketView('list')} data-testid="ticket-view-list"
              className={`px-2.5 py-1 rounded-full inline-flex items-center gap-1 text-[11px] font-medium ${ticketView === 'list' ? 'bg-[#0f5c3d] text-white' : 'text-[#0b1a2e]/70 hover:bg-black/5'}`}>
              <ListIcon className="w-3 h-3" /> List
            </button>
            <button onClick={() => setTicketView('kanban')} data-testid="ticket-view-kanban"
              className={`px-2.5 py-1 rounded-full inline-flex items-center gap-1 text-[11px] font-medium ${ticketView === 'kanban' ? 'bg-[#0f5c3d] text-white' : 'text-[#0b1a2e]/70 hover:bg-black/5'}`}>
              <LayoutGrid className="w-3 h-3" /> Kanban
            </button>
          </div>
          <button onClick={() => setShowNew(true)} data-testid="new-ticket-btn" className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 sm:py-2.5 rounded-full bg-[#0f5c3d] text-white text-sm font-medium hover:bg-[#0b1a2e]">
            <Plus className="w-4 h-4" /> New<span className="hidden sm:inline"> ticket</span>
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard label="Total" value={stats.total_tickets} icon={TicketIcon} />
          <StatCard label="Open" value={stats.open_tickets} icon={Circle} accent="text-blue-600" />
          <StatCard label="Closed" value={stats.closed_tickets} icon={CheckCircle2} accent="text-emerald-600" />
          <StatCard label="Pending tasks" value={stats.pending_tasks} icon={ListChecks} accent="text-amber-600" />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-black/5 rounded-2xl p-3 mb-4 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b1a2e]/40" />
          <input
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder="Search tickets…"
            data-testid="ticket-search"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#0f5c3d]/15 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
          />
        </div>
        <FilterSelect value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} label="Status" options={[['', 'All statuses'], ...Object.entries(STATUS_META).map(([k, v]) => [k, v.label])]} />
        <FilterSelect value={filters.priority} onChange={(v) => setFilters({ ...filters, priority: v })} label="Priority" options={[['', 'All priorities'], ...Object.entries(PRIORITY_META).map(([k, v]) => [k, v.label])]} />
        <FilterSelect value={filters.department_id} onChange={(v) => setFilters({ ...filters, department_id: v })} label="Department" options={[['', 'All departments'], ...depts.map((d) => [d.id, d.name])]} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {ticketView === 'kanban' ? (
          <div className="lg:col-span-12">
            <TicketsKanban tickets={tickets} deptLookup={deptLookup} userLookup={userLookup}
              statusFilter={filters.status}
              onOpen={(t) => { setTicketView('list'); setSelectedId(t.id); }}
              onMove={async (t, newStatus) => {
                try {
                  const updated = await updateTicket(t.id, { status: newStatus });
                  setTickets((arr) => arr.map((x) => x.id === t.id ? { ...x, ...updated } : x));
                  toast({ title: `Moved to ${STATUS_META[newStatus]?.label || newStatus}` });
                  await reloadStats();
                } catch { toast({ title: 'Move failed', variant: 'destructive' }); reloadTickets(); }
              }} />
          </div>
        ) : (
        <>
        {/* Ticket list — hidden on mobile when a ticket is selected */}
        <div className={`lg:col-span-4 bg-white border border-black/5 rounded-2xl overflow-hidden max-h-[75vh] flex flex-col ${selectedId ? 'hidden lg:flex' : 'flex'}`}>
          <div className="px-4 py-2.5 border-b border-black/5 text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/60">
            {loadingList ? 'Loading…' : `${tickets.length} tickets`}
          </div>
          <div className="overflow-y-auto flex-1">
            {tickets.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No tickets found.</div>
            ) : tickets.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedId(t.id)}
                data-testid="ticket-row"
                className={`w-full text-left px-4 py-3 border-b border-black/5 hover:bg-[#f7fbf8] transition ${selectedId === t.id ? 'bg-[#f7fbf8]' : ''}`}
              >
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="font-mono font-semibold text-[#0f5c3d]">{t.ticket_no}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_META[t.status]?.color || 'bg-slate-100'}`}>{STATUS_META[t.status]?.label || t.status}</span>
                  <span className={`text-[10px] font-semibold uppercase ${PRIORITY_META[t.priority]?.color}`}>{PRIORITY_META[t.priority]?.label}</span>
                </div>
                <div className="mt-1 font-medium text-sm text-[#0b1a2e] line-clamp-2">{t.title}</div>
                <div className="mt-1 text-[11px] text-[#0b1a2e]/50 flex items-center gap-2 flex-wrap">
                  <span>{fmt(t.created_at)}</span>
                  {(t.last_activity_at || t.updated_at) && String(t.last_activity_at || t.updated_at).slice(0, 16) !== String(t.created_at).slice(0, 16) && (
                    <span className="text-[#0f5c3d] font-semibold" title="Latest activity on this ticket">· active {fmt(t.last_activity_at || t.updated_at)}</span>
                  )}
                  {(t.unread_count || 0) > 0 && (
                    <span data-testid="ticket-unread-badge"
                      title={`${t.unread_count} unread message${t.unread_count !== 1 ? 's' : ''}`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#0f5c3d] text-white text-[10px] font-bold">
                      <span className="w-1 h-1 rounded-full bg-white" /> {t.unread_count}
                    </span>
                  )}
                  {t.all_departments && <span className="px-1.5 py-0.5 bg-[#0f5c3d]/10 text-[#0f5c3d] rounded font-semibold">All depts</span>}
                  {(t.department_ids || []).slice(0, 2).map((did) => (
                    <span key={did}>· {deptLookup[did]?.name || 'dept'}</span>
                  ))}
                  {(t.department_ids || []).length > 2 && <span>· +{t.department_ids.length - 2} more</span>}
                  {(t.assignee_ids || []).length > 0 && (
                    <span>· {(t.assignee_ids || []).map((aid) => userLookup[aid]?.name).filter(Boolean).slice(0, 2).join(', ')}
                      {t.assignee_ids.length > 2 && ` +${t.assignee_ids.length - 2}`}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Ticket detail — hidden on mobile when no ticket is selected */}
        <div className={`lg:col-span-8 ${selected ? 'block' : 'hidden lg:block'}`}>
          {selected ? (
            <TicketDetail
              key={selected.id}
              ticket={selected}
              depts={depts}
              users={users}
              userLookup={userLookup}
              currentUserId={user?.user_id}
              currentRole={user?.role}
              onBack={() => setSelectedId(null)}
              onChange={async (updated) => {
                setTickets((arr) => arr.map((x) => x.id === updated.id ? updated : x));
                await reloadStats();
              }}
              onDelete={async () => {
                setSelectedId(null);
                await reloadTickets();
                await reloadStats();
              }}
            />
          ) : (
            <div className="bg-white border border-black/5 rounded-2xl p-16 text-center text-[#0b1a2e]/50">
              <TicketIcon className="w-10 h-10 mx-auto mb-3 text-[#0b1a2e]/30" />
              Select a ticket on the left or create a new one.
            </div>
          )}
        </div>
        </>
        )}
      </div>

      {showNudgeSettings && <NudgeSettingsModal onClose={() => setShowNudgeSettings(false)} />}

      {showNew && (
        <NewTicketModal
          onClose={() => setShowNew(false)}
          onCreated={onTicketCreated}
          depts={depts}
          users={users}
        />
      )}
      {showDeptMgr && (
        <DepartmentManager
          onClose={() => setShowDeptMgr(false)}
          depts={depts}
          onChange={async () => {
            const r = await listDepartments();
            setDepts(r.data || []);
          }}
        />
      )}
    </div>
  );
};


// =========================================================
// Sub-components
// =========================================================

const StatCard = ({ label, value, icon: Icon, accent = 'text-[#0f5c3d]' }) => (
  <div className="bg-white border border-black/5 rounded-2xl p-4">
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/60">{label}</span>
      <Icon className={`w-4 h-4 ${accent}`} />
    </div>
    <div className="mt-2 text-2xl font-semibold text-[#0b1a2e]">{value}</div>
  </div>
);

const FilterSelect = ({ value, onChange, label, options }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="px-3 py-2 rounded-lg border border-[#0f5c3d]/15 text-sm bg-white outline-none">
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

const NewTicketModal = ({ onClose, onCreated, depts, users }) => {
  const [form, setForm] = useState({
    title: '', description: '', priority: 'medium', category: '',
    department_ids: [], assignee_ids: [], all_departments: false,
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const t = await createTicket({
        ...form,
        department_ids: form.all_departments ? [] : form.department_ids,
      });
      toast({ title: `Created ${t.ticket_no}` });
      onCreated(t);
    } catch (err) {
      toast({ title: 'Create failed', variant: 'destructive', description: err?.response?.data?.detail || '' });
    } finally { setSaving(false); }
  };
  return (
    <Modal onClose={onClose}>
      <form onSubmit={submit}>
        <ModalHeader title="New ticket" onClose={onClose} />
        <div className="p-6 space-y-4">
          <FormField label="Title">
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ticket-title-input" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
          </FormField>
          <FormField label="Description">
            <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ticket-desc-input" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm resize-y" />
          </FormField>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Priority">
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="ticket-priority" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm bg-white">
                {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </FormField>
            <FormField label="Category">
              <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. IT / Finance / HR" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
            </FormField>
          </div>
          <FormField label="Departments">
            <MultiSelect
              testId="ticket-departments"
              placeholder="Select departments…"
              options={depts.map((d) => ({ value: d.id, label: d.name }))}
              values={form.department_ids}
              onChange={(v) => setForm({ ...form, department_ids: v })}
              allOption={{
                label: 'All departments (broadcast)',
                checked: form.all_departments,
                onToggle: (v) => setForm({ ...form, all_departments: v, department_ids: v ? [] : form.department_ids }),
              }}
              emptyLabel="No department selected"
            />
          </FormField>
          <FormField label="Assign to">
            <MultiSelect
              testId="ticket-assignees"
              placeholder="Select assignees…"
              options={users.map((u) => ({ value: u.user_id, label: u.name, hint: u.role }))}
              values={form.assignee_ids}
              onChange={(v) => setForm({ ...form, assignee_ids: v })}
              emptyLabel="Unassigned"
            />
          </FormField>
        </div>
        <ModalFooter>
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm hover:bg-black/5">Cancel</button>
          <button type="submit" disabled={saving} data-testid="submit-new-ticket" className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#0f5c3d] text-white text-sm font-medium disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create ticket
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

const TicketDetail = ({ ticket, depts, users, userLookup, currentUserId, currentRole, onChange, onDelete, onBack }) => {
  const [tab, setTab] = useState('messages');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...ticket });
  const [closeRating, setCloseRating] = useState(null);   // {score, comment} when prompt open
  const [existingRating, setExistingRating] = useState(null);

  useEffect(() => { setForm({ ...ticket }); setEditing(false); }, [ticket.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load existing rating if ticket is closed/won
  useEffect(() => {
    if (['closed', 'won'].includes(ticket.status)) {
      listTicketRatings(ticket.id).then((r) => {
        const mine = (r.data || []).find((x) => x.rater_id === currentUserId);
        setExistingRating(mine || null);
      }).catch(() => {});
    } else {
      setExistingRating(null);
    }
  }, [ticket.id, ticket.status, currentUserId]);

  const patch = async (fields) => {
    try {
      const wasOpen = !['closed', 'won'].includes(ticket.status);
      const updated = await updateTicket(ticket.id, fields);
      onChange(updated);
      toast({ title: 'Ticket updated' });
      // Auto-prompt for rating when status transitions to closed/won and I am the creator
      if (wasOpen && ['closed', 'won'].includes(updated.status)
          && updated.created_by === currentUserId && !existingRating) {
        setCloseRating({ score: 5, comment: '' });
      }
    } catch (err) { toast({ title: err?.response?.data?.detail || 'Update failed', variant: 'destructive' }); }
  };

  const submitCloseRating = async () => {
    try {
      await rateTicket(ticket.id, closeRating.score, closeRating.comment);
      toast({ title: `Rated ${closeRating.score}★ · thanks!` });
      setCloseRating(null);
      setExistingRating({ score: closeRating.score, comment: closeRating.comment });
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Rating failed', variant: 'destructive' }); }
  };

  const saveEdits = async () => {
    await patch({
      title: form.title,
      description: form.description,
      category: form.category,
    });
    setEditing(false);
  };

  const removeTicket = async () => {
    if (!window.confirm(`Delete ${ticket.ticket_no}?`)) return;
    try { await deleteTicket(ticket.id); toast({ title: 'Ticket deleted' }); onDelete(); }
    catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const followUp = async () => {
    const note = window.prompt('Add an optional note for the follow-up (press OK to nudge without a note):', '');
    if (note === null) return; // user cancelled
    try {
      await followUpTicket(ticket.id, note || '', true);
      toast({ title: 'Follow-up sent', description: `Nudged ${(ticket.assignee_ids || []).length} assignee${(ticket.assignee_ids || []).length !== 1 ? 's' : ''}` });
      if (tab === 'messages') {
        // triggers MessagesTab reload via key change
        onChange({ ...ticket, updated_at: new Date().toISOString() });
      }
    } catch (err) {
      toast({ title: 'Follow-up failed', variant: 'destructive', description: err?.response?.data?.detail || '' });
    }
  };

  return (
    <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
      {existingRating && (
        <div className="px-6 py-2 bg-emerald-50 border-b border-emerald-100 text-xs flex items-center gap-2">
          <span className="text-emerald-800">Rated:</span>
          {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n} className={`w-3.5 h-3.5 ${n <= existingRating.score ? 'text-amber-500 fill-amber-500' : 'text-emerald-800/20'}`} />
          ))}
          {existingRating.comment && <span className="text-emerald-800/70 italic ml-1">"{existingRating.comment}"</span>}
        </div>
      )}
      {closeRating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="close-rating-modal">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-5 py-4 bg-[#0f5c3d] text-white flex items-center gap-2">
              <Star className="w-5 h-5" />
              <div>
                <div className="text-lg font-medium">Rate this engagement</div>
                <div className="text-[11px] opacity-80">Ticket {ticket.ticket_no} is now {ticket.status}. How did the team do?</div>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <div className="flex items-center justify-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setCloseRating({ ...closeRating, score: n })}
                    data-testid={`close-rate-${n}`}>
                    <Star className={`w-9 h-9 ${n <= closeRating.score ? 'text-amber-500 fill-amber-500' : 'text-slate-300 hover:text-amber-300'}`} />
                  </button>
                ))}
              </div>
              <textarea value={closeRating.comment} onChange={(e) => setCloseRating({ ...closeRating, comment: e.target.value })}
                data-testid="close-rate-comment"
                placeholder="Any feedback for the team? (optional)"
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
            </div>
            <div className="px-5 py-3 border-t border-black/5 bg-[#f7fbf8] flex justify-end gap-2">
              <button onClick={() => setCloseRating(null)} className="px-3 py-1.5 text-sm">Later</button>
              <button onClick={submitCloseRating} data-testid="close-rate-submit"
                className="px-4 py-1.5 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold">Submit rating</button>
            </div>
          </div>
        </div>
      )}
      <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-black/5">
        {onBack && (
          <button onClick={onBack} data-testid="ticket-back-btn"
            className="lg:hidden mb-2 inline-flex items-center gap-1 text-xs font-medium text-[#0f5c3d] hover:underline">
            ← Back to tickets
          </button>
        )}
        <div className="flex items-center gap-1.5 sm:gap-2 text-xs flex-wrap">
          <span className="font-mono font-semibold text-[#0f5c3d]">{ticket.ticket_no}</span>
          <select
            value={ticket.status}
            onChange={(e) => patch({ status: e.target.value })}
            data-testid="ticket-status-select"
            className={`px-2 py-1 rounded-full text-[11px] font-semibold border-0 ${STATUS_META[ticket.status]?.color}`}
          >
            {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select
            value={ticket.priority}
            onChange={(e) => patch({ priority: e.target.value })}
            className={`px-2 py-1 rounded text-[11px] font-semibold uppercase bg-transparent border border-[#0f5c3d]/15 ${PRIORITY_META[ticket.priority]?.color}`}
          >
            {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <div className="flex-1 min-w-0" />
          <button onClick={followUp} data-testid="ticket-followup-btn" title="Follow up (nudge assignees)" className="p-1.5 rounded-lg hover:bg-[#f7fbf8] text-[#0f5c3d]"><Bell className="w-3.5 h-3.5" /></button>
          <button onClick={() => setEditing((v) => !v)} className="p-1.5 rounded-lg hover:bg-black/5" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
          <button onClick={removeTicket} className="p-1.5 rounded-lg text-red-600 hover:bg-red-50" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
        {editing ? (
          <div className="mt-3 space-y-2">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-lg font-medium" />
            <textarea rows={3} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm resize-y" />
            <div className="flex gap-2">
              <button onClick={saveEdits} className="px-4 py-2 rounded-lg bg-[#0f5c3d] text-white text-sm">Save</button>
              <button onClick={() => setEditing(false)} className="px-4 py-2 rounded-lg text-sm hover:bg-black/5">Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <h2 className="mt-3 text-xl font-semibold text-[#0b1a2e]">{ticket.title}</h2>
            {ticket.description && <p className="mt-2 text-sm text-[#0b1a2e]/70 whitespace-pre-wrap">{ticket.description}</p>}
          </>
        )}
        <div className="mt-4 space-y-3 text-xs">
          <div className="flex flex-wrap gap-3 items-start">
            <div className="w-full sm:min-w-[220px] sm:flex-1">
              <label className="block text-[10px] uppercase tracking-widest text-[#0b1a2e]/50 mb-1 font-semibold">Assignees</label>
              <MultiSelect
                testId="ticket-detail-assignees"
                placeholder="Select assignees…"
                options={users.map((u) => ({ value: u.user_id, label: u.name, hint: u.role }))}
                values={ticket.assignee_ids || []}
                onChange={(v) => patch({ assignee_ids: v })}
                emptyLabel="Unassigned"
              />
            </div>
            <div className="w-full sm:min-w-[220px] sm:flex-1">
              <label className="block text-[10px] uppercase tracking-widest text-[#0b1a2e]/50 mb-1 font-semibold">Departments</label>
              <MultiSelect
                testId="ticket-detail-departments"
                placeholder="Select departments…"
                options={depts.map((d) => ({ value: d.id, label: d.name }))}
                values={ticket.department_ids || []}
                onChange={(v) => patch({ department_ids: v })}
                allOption={{
                  label: 'All departments',
                  checked: !!ticket.all_departments,
                  onToggle: (v) => patch({ all_departments: v, department_ids: v ? [] : (ticket.department_ids || []) }),
                }}
                emptyLabel="No department"
              />
            </div>
          </div>
          <div className="text-[#0b1a2e]/50">
            Created {fmt(ticket.created_at)}
            {(ticket.last_activity_at || ticket.updated_at) && String(ticket.last_activity_at || ticket.updated_at).slice(0, 16) !== String(ticket.created_at).slice(0, 16) && (
              <> · <span className="text-[#0f5c3d] font-medium">Last activity {fmt(ticket.last_activity_at || ticket.updated_at)}</span></>
            )}
          </div>
        </div>
      </div>

      <div className="flex border-b border-black/5 bg-[#f7fbf8] overflow-x-auto">
        {[
          ['messages', <MessageSquare key="i" className="w-4 h-4" />, 'Messages'],
          ['tasks', <ListChecks key="i" className="w-4 h-4" />, 'Tasks'],
          ['docs', <span key="i" className="text-base">📄</span>, 'Docs'],
          ['files', <Paperclip key="i" className="w-4 h-4" />, 'Files'],
        ].map(([k, icon, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`crm-tab-${k}`}
            className={`px-3 sm:px-4 py-2.5 text-sm font-medium inline-flex items-center gap-1.5 transition whitespace-nowrap relative ${tab === k ? 'text-[#0f5c3d] bg-white border-b-2 border-[#0f5c3d]' : 'text-[#0b1a2e]/60 hover:text-[#0b1a2e]'}`}
          >
            {icon} {label}
            {k === 'messages' && (ticket.unread_count || 0) > 0 && (
              <span data-testid="messages-tab-dot"
                title={`${ticket.unread_count} unread`}
                className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-[#0f5c3d] text-white text-[9px] font-bold">
                {ticket.unread_count}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="p-4 sm:p-6">
        {tab === 'messages' && <MessagesPanel ticketId={ticket.id} users={users} userLookup={userLookup} currentUserId={currentUserId} />}
        {tab === 'tasks' && <TasksPanel ticketId={ticket.id} users={users} userLookup={userLookup} />}
        {tab === 'docs' && <DocsTab ticketId={ticket.id} />}
        {tab === 'files' && <FilesPanel ticketId={ticket.id} userLookup={userLookup} currentUserId={currentUserId} />}
      </div>
    </div>
  );
};

const MetaSelect = ({ label, value, onChange, options }) => (
  <label className="inline-flex items-center gap-1.5 text-xs">
    <span className="text-[#0b1a2e]/50 uppercase tracking-widest text-[10px] font-semibold">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="border border-[#0f5c3d]/15 rounded px-2 py-1 bg-white text-xs">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </label>
);

const ReadDots = ({ readBy = [], authorId, userLookup, maxDots = 4 }) => {
  const readers = (readBy || []).filter((r) => r.user_id && r.user_id !== authorId);
  if (readers.length === 0) return null;
  const shown = readers.slice(0, maxDots);
  const rest = readers.length - shown.length;
  return (
    <div className="flex items-center gap-0.5 mt-1" data-testid="read-receipts" title={`Read by ${readers.map((r) => userLookup[r.user_id]?.name || 'someone').join(', ')}`}>
      <span className="text-[9px] text-[#0b1a2e]/40 mr-0.5">Read</span>
      {shown.map((r) => {
        const u = userLookup[r.user_id];
        const initial = (u?.name || u?.email || '?').charAt(0).toUpperCase();
        return (
          <span key={r.user_id} data-testid="read-dot"
            className="w-4 h-4 rounded-full bg-[#0f5c3d] text-white text-[8px] font-bold flex items-center justify-center border border-white -ml-1 first:ml-0"
            style={{ zIndex: 1 }}>{initial}</span>
        );
      })}
      {rest > 0 && <span className="text-[9px] text-[#0b1a2e]/50 ml-0.5">+{rest}</span>}
    </div>
  );
};

const MessagesPanel = ({ ticketId, users = [], userLookup, currentUserId }) => {
  const [items, setItems] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  // Mention picker + visibility (mirrors TaskDetailDialog UX)
  const [mentionQuery, setMentionQuery] = useState(null); // { text, start }
  const [mentionIdx, setMentionIdx] = useState(0);
  const [mentions, setMentions] = useState([]); // user_ids from picker
  const [visibility, setVisibility] = useState('everyone'); // 'everyone' | 'private' | 'selected'
  const [visibleTo, setVisibleTo] = useState([]);
  const [typers, setTypers] = useState([]);
  const lastTypingSentRef = useRef(0);
  const scrollRef = useRef();
  const inputRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listMessages(ticketId);
      setItems(r.data || []);
      // Fire-and-forget: mark visible messages as read
      markMessagesRead(ticketId).catch(() => {});
    } catch { toast({ title: 'Failed to load messages', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [ticketId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [items]);

  // Poll typing indicators every 3s while this panel is mounted
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await getTyping(ticketId);
        if (!cancelled) setTypers(r.data || []);
      } catch { /* silent */ }
    };
    tick();
    const int = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(int); };
  }, [ticketId]);

  // Throttled typing heartbeat — fires at most once every 2.5s while user is typing
  const pingTyping = () => {
    const now = Date.now();
    if (now - lastTypingSentRef.current < 2500) return;
    lastTypingSentRef.current = now;
    setTyping(ticketId).catch(() => {});
  };

  const send = async (e) => {
    e?.preventDefault?.();
    if (!text.trim()) return;
    setSending(true);
    try {
      const payload = {
        ticket_id: ticketId,
        body: text,
        is_private: visibility === 'private',
        mentions,
      };
      if (visibility === 'selected') {
        payload.visible_to_ids = Array.from(new Set([...visibleTo, ...mentions]));
      }
      await createMessage(payload);
      setText('');
      setMentions([]);
      setVisibility('everyone');
      setVisibleTo([]);
      setMentionQuery(null);
      load();
    } catch { toast({ title: 'Send failed', variant: 'destructive' }); }
    finally { setSending(false); }
  };

  const filteredMentionOptions = mentionQuery
    ? users.filter((u) => (u.name || u.email || '').toLowerCase().includes(mentionQuery.text)).slice(0, 8)
    : [];

  const insertMention = (u) => {
    const before = text.slice(0, mentionQuery.start);
    const afterStart = mentionQuery.start + 1 + mentionQuery.text.length;
    const after = text.slice(afterStart);
    const handle = (u.email || '').split('@')[0] || u.name || '';
    const insert = `@${handle} `;
    setText(before + insert + after);
    setMentions((prev) => Array.from(new Set([...prev, u.user_id])));
    setMentionQuery(null);
    setTimeout(() => {
      if (inputRef.current) {
        const pos = before.length + insert.length;
        inputRef.current.selectionStart = inputRef.current.selectionEnd = pos;
        inputRef.current.focus();
      }
    }, 0);
  };

  return (
    <div className="flex flex-col h-[60vh] sm:h-[500px]">
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3 pr-1">
        {loading ? (
          <div className="text-center text-[#0b1a2e]/50 py-8"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center text-[#0b1a2e]/50 py-8 text-sm">No messages yet — start the conversation below.</div>
        ) : items.map((m) => {
          const mine = m.created_by === currentUserId;
          const restricted = Array.isArray(m.visible_to_ids);
          return (
            <div key={m.id} data-testid="crm-message" className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] sm:max-w-[75%] rounded-xl px-3 py-2 ${mine ? 'bg-[#0f5c3d] text-white' : 'bg-[#f7fbf8] border border-black/5'}`}>
                <div className={`text-[10px] font-semibold uppercase tracking-widest flex items-center gap-1 flex-wrap ${mine ? 'text-white/70' : 'text-[#0f5c3d]'}`}>
                  <span>{mine ? 'You' : (userLookup[m.created_by]?.name || 'User')}</span>
                  {m.is_private && <span className="inline-flex items-center gap-0.5"><Lock className="w-3 h-3" /> private</span>}
                  {restricted && (
                    <span data-testid="msg-restricted-badge"
                      title={`Visible to ${(m.visible_to_ids || []).map((id) => userLookup[id]?.name || 'someone').join(', ') || 'author only'}`}
                      className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded-full ${mine ? 'bg-white/20' : 'bg-amber-100 text-amber-800'}`}>
                      🔒 {(m.visible_to_ids || []).length + 1}
                    </span>
                  )}
                </div>
                <div className={`mt-1 text-sm whitespace-pre-wrap ${mine ? 'text-white' : 'text-[#0b1a2e]'}`}>{m.body}</div>
                <div className={`mt-1 text-[10px] ${mine ? 'text-white/60' : 'text-[#0b1a2e]/50'}`}>{fmt(m.created_at)}</div>
                {mine && <ReadDots readBy={m.read_by} authorId={m.created_by} userLookup={userLookup} />}
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="mt-3 border-t border-black/5 pt-3">
        {typers.length > 0 && (
          <div data-testid="typing-indicator" className="text-[11px] text-[#0f5c3d] italic mb-1 flex items-center gap-1">
            <span className="inline-flex gap-0.5">
              <span className="w-1 h-1 rounded-full bg-[#0f5c3d] animate-pulse" style={{ animationDelay: '0ms' }} />
              <span className="w-1 h-1 rounded-full bg-[#0f5c3d] animate-pulse" style={{ animationDelay: '150ms' }} />
              <span className="w-1 h-1 rounded-full bg-[#0f5c3d] animate-pulse" style={{ animationDelay: '300ms' }} />
            </span>
            <span>
              {typers.length === 1
                ? `${typers[0].name} is typing…`
                : typers.length === 2
                  ? `${typers[0].name} and ${typers[1].name} are typing…`
                  : `${typers[0].name} and ${typers.length - 1} others are typing…`}
            </span>
          </div>
        )}
        <div className="flex items-end gap-2 relative">
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={text}
              onChange={(e) => {
                const v = e.target.value;
                setText(v);
                if (v.trim().length > 0) pingTyping();
                const caret = e.target.selectionStart ?? v.length;
                const before = v.slice(0, caret);
                const mObj = before.match(/(?:^|\s)@([\w.@-]*)$/);
                if (mObj) {
                  setMentionQuery({ text: mObj[1].toLowerCase(), start: before.length - mObj[1].length - 1 });
                  setMentionIdx(0);
                } else {
                  setMentionQuery(null);
                }
              }}
              onKeyDown={(e) => {
                if (mentionQuery && filteredMentionOptions.length > 0 && ['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'].includes(e.key)) {
                  if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx((i) => (i + 1) % filteredMentionOptions.length); return; }
                  if (e.key === 'ArrowUp')   { e.preventDefault(); setMentionIdx((i) => (i - 1 + filteredMentionOptions.length) % filteredMentionOptions.length); return; }
                  if (e.key === 'Escape')    { e.preventDefault(); setMentionQuery(null); return; }
                  if (e.key === 'Enter' || e.key === 'Tab') {
                    e.preventDefault();
                    insertMention(filteredMentionOptions[mentionIdx]);
                    return;
                  }
                }
                if (e.key === 'Enter' && !e.shiftKey && !mentionQuery) {
                  e.preventDefault();
                  send(e);
                }
              }}
              rows={2}
              placeholder={visibility === 'private' ? 'Private note (only admins & ticket owner/assignee)…' : 'Message the thread… type @ to mention (Enter to send)'}
              data-testid="crm-message-input"
              className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm resize-none"
            />
            {mentionQuery && filteredMentionOptions.length > 0 && (
              <div
                data-testid="crm-mention-popover"
                className="absolute left-2 -top-1 -translate-y-full z-10 bg-white border border-[#0f5c3d]/25 rounded-lg shadow-lg w-64 max-h-64 overflow-y-auto">
                <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-[#0b1a2e]/50 border-b border-black/5">Mention someone</div>
                {filteredMentionOptions.map((u, i) => (
                  <button
                    key={u.user_id}
                    type="button"
                    data-testid={`crm-mention-option-${u.user_id}`}
                    onMouseDown={(e) => { e.preventDefault(); insertMention(u); }}
                    className={`w-full text-left px-2 py-1.5 flex items-center gap-2 text-sm ${i === mentionIdx ? 'bg-[#f7fbf8]' : 'hover:bg-[#f7fbf8]'}`}
                  >
                    <span className="w-6 h-6 rounded-full bg-[#0f5c3d] text-white text-[10px] font-semibold flex items-center justify-center flex-shrink-0">
                      {(u.name || u.email || '?').charAt(0).toUpperCase()}
                    </span>
                    <span className="flex-1 min-w-0 truncate text-[#0b1a2e]">{u.name || u.email}</span>
                    {u.role && <span className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/50">{u.role}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button type="submit" disabled={sending || !text.trim()} data-testid="crm-send-message" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-[#0f5c3d] text-white text-sm disabled:opacity-40 self-start">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-[#0b1a2e]/50 uppercase tracking-widest text-[10px] font-semibold">Visible to</span>
          <button type="button" onClick={() => setVisibility('everyone')} data-testid="msg-vis-everyone"
            className={`px-2.5 py-1 rounded-full font-medium ${visibility === 'everyone' ? 'bg-[#0f5c3d] text-white' : 'bg-white border border-[#0f5c3d]/20 text-[#0b1a2e]'}`}>
            Everyone
          </button>
          <button type="button" onClick={() => setVisibility('private')} data-testid="msg-vis-private"
            className={`px-2.5 py-1 rounded-full font-medium inline-flex items-center gap-1 ${visibility === 'private' ? 'bg-[#0f5c3d] text-white' : 'bg-white border border-[#0f5c3d]/20 text-[#0b1a2e]'}`}>
            <Lock className="w-3 h-3" /> Owner + Admins
          </button>
          <button type="button" onClick={() => setVisibility('selected')} data-testid="msg-vis-selected"
            className={`px-2.5 py-1 rounded-full font-medium ${visibility === 'selected' ? 'bg-[#0f5c3d] text-white' : 'bg-white border border-[#0f5c3d]/20 text-[#0b1a2e]'}`}>
            Selected people
          </button>
          {mentions.length > 0 && (
            <span className="text-[10px] text-[#0f5c3d]" data-testid="msg-mention-chip-count">
              {mentions.length} @mention{mentions.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {visibility === 'selected' && (
          <div className="mt-2 p-2 bg-[#f7fbf8] rounded-lg border border-[#0f5c3d]/15 max-h-40 overflow-y-auto" data-testid="msg-visibility-picker">
            <div className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 mb-1 font-semibold">
              Who can see this message? (@mentioned people auto-included)
            </div>
            {users.map((u) => {
              const forced = mentions.includes(u.user_id);
              const checked = visibleTo.includes(u.user_id) || forced;
              return (
                <label key={u.user_id} className="flex items-center gap-2 py-0.5 px-1 hover:bg-white rounded cursor-pointer text-sm">
                  <input type="checkbox" checked={checked} disabled={forced}
                    onChange={() => setVisibleTo((prev) => prev.includes(u.user_id)
                      ? prev.filter((x) => x !== u.user_id)
                      : [...prev, u.user_id])}
                    data-testid={`msg-visible-to-${u.user_id}`}
                    className="w-4 h-4 accent-[#0f5c3d]" />
                  <span className="w-5 h-5 rounded-full bg-[#0f5c3d] text-white text-[9px] font-semibold flex items-center justify-center">
                    {(u.name || u.email || '?').charAt(0).toUpperCase()}
                  </span>
                  <span className="text-[#0b1a2e]">{u.name || u.email}</span>
                  {forced && <span className="ml-auto text-[9px] uppercase tracking-widest text-[#0f5c3d]">@mentioned</span>}
                </label>
              );
            })}
          </div>
        )}
      </form>
    </div>
  );
};

const TICKET_KANBAN_COLUMNS = [
  { key: 'open', title: 'Open', color: 'bg-blue-500' },
  { key: 'assigned', title: 'Assigned', color: 'bg-indigo-500' },
  { key: 'in_progress', title: 'In Progress', color: 'bg-amber-500' },
  { key: 'review', title: 'Review', color: 'bg-purple-500' },
  { key: 'won', title: 'Won', color: 'bg-emerald-600' },
  { key: 'closed', title: 'Closed', color: 'bg-slate-500' },
];

const TicketsKanban = ({ tickets, deptLookup, userLookup, onOpen, onMove, statusFilter = '' }) => {
  const [drag, setDrag] = useState(null);
  const columns = statusFilter
    ? TICKET_KANBAN_COLUMNS.filter((c) => c.key === statusFilter)
    : TICKET_KANBAN_COLUMNS;
  const grouped = columns.reduce((acc, c) => ({ ...acc, [c.key]: [] }), {});
  for (const t of tickets) {
    if (grouped[t.status] !== undefined) grouped[t.status].push(t);
    else if (!statusFilter) (grouped.open || (grouped.open = [])).push(t);
  }
  const cols = columns.length === 1 ? 'grid-cols-1' : columns.length <= 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-6';
  return (
    <div className={`grid ${cols} gap-3`} data-testid="tickets-kanban">
      {TICKET_KANBAN_COLUMNS.map((col) => (
        columns.find((c) => c.key === col.key) ? null : null
      ))}
      {columns.map((col) => (
        <div key={col.key}
          onDragOver={(e) => { if (drag) e.preventDefault(); }}
          onDrop={() => { if (drag && drag.status !== col.key) { onMove(drag, col.key); } setDrag(null); }}
          className={`bg-[#f7fbf8] rounded-xl min-h-[280px] ${drag && drag.status !== col.key ? 'ring-2 ring-[#0f5c3d]/40' : ''}`}
          data-testid={`tickets-kanban-col-${col.key}`}>
          <div className="flex items-center gap-2 px-3 py-2 border-b border-black/5">
            <span className={`w-2 h-2 rounded-full ${col.color}`} />
            <span className="text-xs font-semibold uppercase tracking-wider text-[#0b1a2e]">{col.title}</span>
            <span className="text-[10px] text-[#0b1a2e]/50 ml-auto">{grouped[col.key].length}</span>
          </div>
          <div className="p-2 space-y-2">
            {grouped[col.key].length === 0 ? (
              <div className="text-[11px] text-[#0b1a2e]/30 text-center py-6 italic">Drop tickets here</div>
            ) : grouped[col.key].map((t) => (
              <div key={t.id}
                data-testid={`tickets-kanban-card-${t.id}`}
                draggable
                onDragStart={() => setDrag(t)}
                onDragEnd={() => setDrag(null)}
                onClick={() => onOpen(t)}
                className="bg-white rounded-lg p-2.5 shadow-sm hover:shadow-md transition cursor-pointer border border-black/5"
              >
                <div className="flex items-start gap-1.5 mb-1">
                  <span className="font-mono text-[10px] font-semibold text-[#0f5c3d]">{t.ticket_no}</span>
                  <span className={`text-[9px] font-semibold uppercase ml-auto ${PRIORITY_META[t.priority]?.color}`}>{PRIORITY_META[t.priority]?.label}</span>
                </div>
                <div className="text-xs text-[#0b1a2e] font-medium leading-snug line-clamp-2">{t.title}</div>
                <div className="mt-1.5 flex items-center gap-1 text-[10px] text-[#0b1a2e]/50 flex-wrap">
                  {(t.assignee_ids || []).slice(0, 1).map((aid) => (
                    <span key={aid} className="truncate max-w-[80px]">{userLookup[aid]?.name?.split(' ')[0] || '—'}</span>
                  ))}
                  {t.assignee_ids?.length > 1 && <span>+{t.assignee_ids.length - 1}</span>}
                  {(t.unread_count || 0) > 0 && (
                    <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded-full bg-[#0f5c3d] text-white text-[9px] font-bold">
                      {t.unread_count}
                    </span>
                  )}
                </div>
                {/* Mobile stage selector — HTML5 DnD doesn't work on touch */}
                <select
                  value={t.status}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => { e.stopPropagation(); if (e.target.value !== t.status) onMove(t, e.target.value); }}
                  data-testid={`tickets-kanban-mobile-stage-${t.id}`}
                  className="md:hidden mt-2 w-full text-[10px] px-2 py-1 rounded border border-[#0f5c3d]/20 bg-[#f7fbf8]"
                >
                  {TICKET_KANBAN_COLUMNS.map((c) => <option key={c.key} value={c.key}>Move to {c.title}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const NudgeSettingsModal = ({ onClose }) => {
  const [form, setForm] = useState({ days_threshold: 7, open_statuses: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  useEffect(() => {
    getNudgeSettings().then((r) => { setForm({ days_threshold: r.days_threshold, open_statuses: r.open_statuses }); }).finally(() => setLoading(false));
  }, []);
  const toggleStatus = (s) => setForm((f) => ({
    ...f,
    open_statuses: f.open_statuses.includes(s) ? f.open_statuses.filter((x) => x !== s) : [...f.open_statuses, s],
  }));
  const save = async () => {
    if (form.open_statuses.length === 0) return toast({ title: 'Pick at least one status', variant: 'destructive' });
    setSaving(true);
    try {
      await putNudgeSettings(form);
      toast({ title: 'Nudge settings saved' });
      onClose();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Save failed', variant: 'destructive' }); }
    finally { setSaving(false); }
  };
  const runNow = async () => {
    setRunning(true);
    try {
      const r = await runNudgeNow();
      toast({ title: 'Nudge run complete', description: `${r.tickets} idle · ${r.emails_sent} email${r.emails_sent !== 1 ? 's' : ''} sent` });
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Run failed', variant: 'destructive' }); }
    finally { setRunning(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="nudge-settings-modal">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex items-center justify-between">
          <div>
            <div className="text-lg font-medium">Inactivity nudges</div>
            <div className="text-xs text-white/60">Auto-email ticket owners when nothing has happened for a while.</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" data-testid="close-nudge-modal"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-5">
          {loading ? (
            <div className="text-center py-8 text-[#0b1a2e]/50"><Loader2 className="w-5 h-5 animate-spin inline" /> Loading…</div>
          ) : (
            <>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#0b1a2e]/60 mb-1.5 font-semibold">Idle days before nudge</label>
                <input type="number" min={1} max={365}
                  value={form.days_threshold}
                  onChange={(e) => setForm({ ...form, days_threshold: parseInt(e.target.value || '7', 10) })}
                  data-testid="nudge-days-input"
                  className="w-24 px-3 py-2 border border-slate-300 rounded text-sm" />
                <span className="ml-2 text-xs text-[#0b1a2e]/60">days without any activity → email creator + assignees</span>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#0b1a2e]/60 mb-1.5 font-semibold">Which statuses count as "open"?</label>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(STATUS_META).map((s) => {
                    const checked = form.open_statuses.includes(s);
                    return (
                      <button key={s} type="button" onClick={() => toggleStatus(s)}
                        data-testid={`nudge-status-${s}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${checked ? 'bg-[#0f5c3d] text-white border-[#0f5c3d]' : 'bg-white text-[#0b1a2e] border-slate-300 hover:border-[#0f5c3d]/40'}`}>
                        {STATUS_META[s]?.label || s}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-1 text-[11px] text-[#0b1a2e]/50">Tickets NOT in one of these statuses will never be nudged.</div>
              </div>
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-black/5">
                <button onClick={save} disabled={saving} data-testid="nudge-save-btn"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm font-medium disabled:opacity-50">
                  {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save
                </button>
                <button onClick={runNow} disabled={running} data-testid="nudge-run-now-btn"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#0f5c3d]/25 text-[#0f5c3d] text-sm font-medium disabled:opacity-50 hover:bg-[#f7fbf8]">
                  {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />} Run once now
                </button>
                <span className="ml-auto text-[10px] text-[#0b1a2e]/40">Runs hourly in the background</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const TICKETS_KANBAN_END_MARKER = null;

const TasksPanel = ({ ticketId, users, userLookup }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(null);
  const [taskView, setTaskView] = useState('list'); // 'list' | 'kanban'

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await listTasks({ ticket_id: ticketId }); setItems(r.data || []); }
    catch { /* silent */ }
    finally { setLoading(false); }
  }, [ticketId]);
  useEffect(() => { load(); }, [load]);

  const toggleDone = async (task) => {
    const next = task.status === 'done' ? 'todo' : 'done';
    await updateTask(task.id, { status: next });
    load();
  };
  const remove = async (task) => {
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    await deleteTask(task.id);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="text-sm text-[#0b1a2e]/60">{items.length} tasks</div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex gap-0.5 rounded-full bg-white border border-[#0f5c3d]/15 p-0.5" data-testid="task-view-toggle">
            <button onClick={() => setTaskView('list')} data-testid="task-view-list"
              className={`px-2.5 py-1 rounded-full inline-flex items-center gap-1 text-[11px] font-medium ${taskView === 'list' ? 'bg-[#0f5c3d] text-white' : 'text-[#0b1a2e]/70 hover:bg-black/5'}`}>
              <ListIcon className="w-3 h-3" /> List
            </button>
            <button onClick={() => setTaskView('kanban')} data-testid="task-view-kanban"
              className={`px-2.5 py-1 rounded-full inline-flex items-center gap-1 text-[11px] font-medium ${taskView === 'kanban' ? 'bg-[#0f5c3d] text-white' : 'text-[#0b1a2e]/70 hover:bg-black/5'}`}>
              <LayoutGrid className="w-3 h-3" /> Kanban
            </button>
          </div>
          <button onClick={() => setShowNew(true)} data-testid="add-task-btn" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs"><Plus className="w-3.5 h-3.5" /> Add task</button>
        </div>
      </div>
      {taskView === 'kanban' ? (
        <KanbanBoard filter={{ ticket_id: ticketId }} />
      ) : loading ? (
        <div className="py-8 text-center text-[#0b1a2e]/50"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center text-sm text-[#0b1a2e]/50">No tasks yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((t) => (
            <div key={t.id} data-testid="task-row" className="flex items-start gap-3 bg-white border border-black/5 rounded-lg p-3">
              <button onClick={() => toggleDone(t)} className="mt-0.5">
                {t.status === 'done' ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Circle className="w-5 h-5 text-[#0b1a2e]/30" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className={`font-medium ${t.status === 'done' ? 'line-through text-[#0b1a2e]/40' : 'text-[#0b1a2e]'}`}>{t.title}</div>
                {t.description && <div className="text-xs text-[#0b1a2e]/60 mt-0.5">{t.description}</div>}
                <div className="mt-1.5 flex items-center gap-2 text-[11px] text-[#0b1a2e]/60 flex-wrap">
                  <span className={`font-semibold uppercase ${PRIORITY_META[t.priority]?.color}`}>{PRIORITY_META[t.priority]?.label}</span>
                  {t.due_date && <span>· due {String(t.due_date).slice(0, 10)}</span>}
                  <span>· created {fmt(t.created_at)}</span>
                  {(t.last_activity_at || t.updated_at) && String(t.last_activity_at || t.updated_at).slice(0, 16) !== String(t.created_at).slice(0, 16) && (
                    <span className="text-[#0f5c3d] font-semibold">· active {fmt(t.last_activity_at || t.updated_at)}</span>
                  )}
                  <span className="inline-flex items-center gap-1 ml-1">
                    <span className="text-[10px] uppercase tracking-wider text-[#0b1a2e]/40">assigned to</span>
                    <select
                      value={t.assignee_id || ''}
                      onChange={async (e) => {
                        await updateTask(t.id, { assignee_id: e.target.value || null });
                        toast({ title: e.target.value ? 'Task reassigned' : 'Task unassigned' });
                        load();
                      }}
                      data-testid="task-assignee-select"
                      className="border border-[#0f5c3d]/15 rounded px-1.5 py-0.5 text-[11px] bg-white"
                    >
                      <option value="">— unassigned —</option>
                      {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                    </select>
                  </span>
                  {(t.history || []).length > 0 && (
                    <button
                      onClick={() => setHistoryOpen(t.id)}
                      data-testid="task-history-btn"
                      className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-[#0f5c3d] hover:underline"
                    >
                      <History className="w-3 h-3" /> {t.history.length} reassign{t.history.length !== 1 ? 's' : ''}
                    </button>
                  )}
                </div>
              </div>
              <button onClick={() => remove(t)} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
      {showNew && (
        <NewTaskModal
          ticketId={ticketId}
          users={users}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }}
        />
      )}
      {historyOpen && (
        <TaskHistoryModal
          task={items.find((t) => t.id === historyOpen)}
          userLookup={userLookup}
          onClose={() => setHistoryOpen(null)}
        />
      )}
    </div>
  );
};

const TaskHistoryModal = ({ task, userLookup, onClose }) => {
  if (!task) return null;
  const history = task.history || [];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#0f5c3d] font-semibold">History</div>
            <div className="text-lg font-semibold text-[#0b1a2e]">Moving the goalpost</div>
            <div className="text-xs text-[#0b1a2e]/60 mt-1">{task.title}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/5 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-3 max-h-[60vh] overflow-y-auto">
          {history.length === 0 ? (
            <div className="text-sm text-[#0b1a2e]/50 text-center py-6">No reassignments yet.</div>
          ) : history.slice().reverse().map((h, idx) => {
            const from = h.from_user_id ? (userLookup[h.from_user_id]?.name || 'someone') : '— unassigned —';
            const to = h.to_user_id ? (userLookup[h.to_user_id]?.name || 'someone') : '— unassigned —';
            return (
              <div key={idx} className="border-l-2 border-[#0f5c3d]/30 pl-4 py-1 relative">
                <div className="absolute -left-[7px] top-2 w-3 h-3 rounded-full bg-[#0f5c3d]" />
                <div className="text-sm text-[#0b1a2e]">
                  <strong>{from}</strong> → <strong className="text-[#0f5c3d]">{to}</strong>
                </div>
                <div className="text-[11px] text-[#0b1a2e]/50 mt-0.5">
                  by {h.actor_name || 'someone'} · {new Date(h.at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const NewTaskModal = ({ ticketId, users, onClose, onCreated }) => {
  const [form, setForm] = useState({
    title: '', description: '', priority: 'medium', due_date: '',
    assign_scope: 'user', assignee_ids: [], role_dept_id: '',
    estimated_hours: 1,
  });
  const [depts, setDepts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    listDepartments().then((r) => setDepts(r.data || [])).catch(() => {});
  }, []);

  const toggleAssignee = (uid) => {
    setForm((f) => ({
      ...f,
      assignee_ids: f.assignee_ids.includes(uid)
        ? f.assignee_ids.filter((x) => x !== uid)
        : [...f.assignee_ids, uid],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.assign_scope === 'user' && form.assignee_ids.length === 0) {
      return toast({ title: 'Pick at least one assignee', variant: 'destructive' });
    }
    if (form.assign_scope === 'role' && !form.role_dept_id) {
      return toast({ title: 'Pick a department for role-based tasks', variant: 'destructive' });
    }
    setSaving(true);
    try {
      const est_min = Math.max(1, Math.round(parseFloat(form.estimated_hours || 1) * 60));
      await createTask({
        ticket_id: ticketId,
        title: form.title,
        description: form.description,
        priority: form.priority,
        due_date: form.due_date || null,
        assign_scope: form.assign_scope,
        assignee_id: form.assign_scope === 'user' ? (form.assignee_ids[0] || null) : null,
        assignee_ids: form.assign_scope === 'user' ? form.assignee_ids : [],
        role_dept_id: form.assign_scope === 'role' ? form.role_dept_id : null,
        estimated_minutes: est_min,
      });
      onCreated();
    } catch (err) { toast({ title: err?.response?.data?.detail || 'Create failed', variant: 'destructive' }); }
    finally { setSaving(false); }
  };
  return (
    <Modal onClose={onClose}>
      <form onSubmit={submit}>
        <ModalHeader title="New task" onClose={onClose} />
        <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
          <FormField label="Title"><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" /></FormField>
          <FormField label="Description"><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm resize-y" /></FormField>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FormField label="Priority">
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm bg-white">
                {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </FormField>
            <FormField label="Due date"><input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" /></FormField>
            <FormField label="Est. hours">
              <input type="number" step="0.25" min="0.25" required value={form.estimated_hours}
                onChange={(e) => setForm({ ...form, estimated_hours: e.target.value })}
                data-testid="task-est-hours"
                className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
            </FormField>
          </div>

          {/* Assignment scope */}
          <FormField label="Assign to">
            <div className="flex gap-2 mb-3">
              <button type="button" onClick={() => setForm({ ...form, assign_scope: 'user' })}
                data-testid="scope-user-btn"
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${form.assign_scope === 'user' ? 'bg-[#0f5c3d] text-white border-[#0f5c3d]' : 'bg-white border-[#0f5c3d]/15 text-[#0b1a2e]'}`}>
                Specific people
              </button>
              <button type="button" onClick={() => setForm({ ...form, assign_scope: 'role' })}
                data-testid="scope-role-btn"
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${form.assign_scope === 'role' ? 'bg-[#0f5c3d] text-white border-[#0f5c3d]' : 'bg-white border-[#0f5c3d]/15 text-[#0b1a2e]'}`}>
                Whole department (first to claim wins)
              </button>
            </div>
            {form.assign_scope === 'user' ? (
              <div className="max-h-40 overflow-y-auto border border-[#0f5c3d]/15 rounded-lg p-2 bg-[#f7fbf8]">
                {users.length === 0 ? (
                  <div className="text-xs text-[#0b1a2e]/50 italic p-2">No users found.</div>
                ) : users.map((u) => (
                  <label key={u.user_id} className="flex items-center gap-2 py-1 px-1 hover:bg-white rounded cursor-pointer text-sm" data-testid="assignee-checkbox">
                    <input type="checkbox" checked={form.assignee_ids.includes(u.user_id)}
                      onChange={() => toggleAssignee(u.user_id)}
                      className="w-4 h-4 accent-[#0f5c3d]" />
                    <span className="w-6 h-6 rounded-full bg-[#0f5c3d] text-white text-[10px] font-semibold flex items-center justify-center">
                      {(u.name || u.email || '?').charAt(0).toUpperCase()}
                    </span>
                    <span className="text-[#0b1a2e]">{u.name || u.email}</span>
                    {u.role && <span className="ml-auto text-[10px] uppercase tracking-widest text-[#0b1a2e]/50">{u.role}</span>}
                  </label>
                ))}
                {form.assignee_ids.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-[#0f5c3d]/10 text-[11px] text-[#0f5c3d]">
                    {form.assignee_ids.length} selected · first person = primary owner, others = co-owners
                  </div>
                )}
              </div>
            ) : (
              <select value={form.role_dept_id} onChange={(e) => setForm({ ...form, role_dept_id: e.target.value })}
                data-testid="dept-select"
                className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm bg-white" required>
                <option value="">— pick a department —</option>
                {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            )}
          </FormField>
        </div>
        <ModalFooter>
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm hover:bg-black/5">Cancel</button>
          <button type="submit" disabled={saving} data-testid="submit-task" className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#0f5c3d] text-white text-sm font-medium disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create task
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

const FilesPanel = ({ ticketId, userLookup, currentUserId }) => {
  const [tree, setTree] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [openFile, setOpenFile] = useState(null);
  const [openVersions, setOpenVersions] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [folder, setFolder] = useState('General');
  const [newFolder, setNewFolder] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const inputRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await listCrmFilesTree(ticketId); setTree(r.folders || {}); }
    catch (err) { console.error('listCrmFilesTree failed', err); }
    finally { setLoading(false); }
  }, [ticketId]);
  useEffect(() => { load(); }, [load]);

  const folders = Object.keys(tree);
  const activeFiles = tree[folder] || [];

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const { url } = await uploadFile(f);
      await createCrmFile({ ticket_id: ticketId, url, filename: f.name, size: f.size, mime_type: f.type, folder });
      toast({ title: 'File uploaded' });
      load();
    } catch { toast({ title: 'Upload failed', variant: 'destructive' }); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ''; }
  };
  const remove = async (f) => {
    if (!window.confirm(`Delete ${f.filename} (v${f.version || 1})?`)) return;
    try { await deleteCrmFile(f.id); load(); } catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };
  const addFolder = () => {
    const name = newFolder.trim();
    if (!name) return;
    if (!folders.includes(name)) setTree({ ...tree, [name]: [] });
    setFolder(name);
    setNewFolder('');
    setShowNewFolder(false);
  };

  const isImg = (f) => (f.mime_type || '').startsWith('image/');
  const isPdf = (f) => (f.mime_type || '').includes('pdf') || (f.filename || '').toLowerCase().endsWith('.pdf');

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex-1 flex flex-wrap gap-1">
          {folders.length === 0 && !showNewFolder && (
            <span className="text-xs text-[#0b1a2e]/50 italic">No folders yet — upload a file to create "General".</span>
          )}
          {folders.map((f) => (
            <button key={f} onClick={() => setFolder(f)}
              data-testid={`folder-tab-${f}`}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${folder === f ? 'bg-[#0f5c3d] text-white' : 'bg-white border border-[#0f5c3d]/20 text-[#0b1a2e] hover:bg-[#f7fbf8]'}`}>
              <Folder className="w-3.5 h-3.5" /> {f}
              <span className="opacity-70">· {tree[f].length}</span>
            </button>
          ))}
          {showNewFolder ? (
            <div className="inline-flex gap-1">
              <input value={newFolder} onChange={(e) => setNewFolder(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addFolder()}
                autoFocus placeholder="Folder name" data-testid="new-folder-input"
                className="px-2 py-1 border border-[#0f5c3d]/20 rounded text-xs" />
              <button onClick={addFolder} className="px-2 py-1 bg-[#0f5c3d] text-white text-xs rounded">Add</button>
              <button onClick={() => { setShowNewFolder(false); setNewFolder(''); }} className="px-2 py-1 text-xs">✕</button>
            </div>
          ) : (
            <button onClick={() => setShowNewFolder(true)} data-testid="new-folder-btn"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-dashed border-[#0f5c3d]/30 text-[#0f5c3d] hover:bg-[#f7fbf8]">
              <FolderPlus className="w-3.5 h-3.5" /> New folder
            </button>
          )}
        </div>
        <label className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs cursor-pointer hover:bg-[#0b1a2e]">
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          Upload to {folder}
          <input ref={inputRef} type="file" className="hidden" onChange={onFile} data-testid="crm-upload-file" />
        </label>
      </div>

      {loading ? (
        <div className="py-8 text-center text-[#0b1a2e]/50"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
      ) : activeFiles.length === 0 ? (
        <div className="py-8 text-center text-sm text-[#0b1a2e]/50 border-2 border-dashed border-[#0f5c3d]/10 rounded-lg">
          No files in <b>{folder}</b>. Drop or upload the first one above.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {activeFiles.map((f) => (
            <div key={f.id} data-testid="crm-file-row" className="bg-white border border-black/5 rounded-lg p-3 flex items-start gap-3">
              <button onClick={() => (isImg(f) || isPdf(f)) && setPreviewFile(f)} className="w-14 h-14 rounded-lg bg-[#f7fbf8] flex items-center justify-center text-[#0f5c3d] shrink-0 overflow-hidden">
                {isImg(f) ? (
                  <img src={resolveFileUrl(f.url)} alt={f.filename} className="w-full h-full object-cover" />
                ) : isPdf(f) ? <FileText className="w-6 h-6 text-red-500" /> : <Paperclip className="w-6 h-6" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#0b1a2e] truncate">
                  {f.filename}
                  {(f.version || 1) > 1 && <span className="ml-1 text-[10px] font-semibold bg-amber-100 text-amber-800 px-1 py-0.5 rounded">v{f.version}</span>}
                </div>
                <div className="text-[11px] text-[#0b1a2e]/50">
                  {f.uploaded_by_name || userLookup[f.uploaded_by]?.name || 'User'} · {fmt(f.uploaded_at)}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(isImg(f) || isPdf(f)) && (
                    <button onClick={() => setPreviewFile(f)} data-testid="file-preview-btn"
                      className="text-xs text-[#0f5c3d] hover:underline inline-flex items-center gap-1">
                      <Eye className="w-3 h-3" /> Preview
                    </button>
                  )}
                  <a href={resolveFileUrl(f.url)} target="_blank" rel="noreferrer" className="text-xs text-[#0f5c3d] hover:underline inline-flex items-center gap-1"><Download className="w-3 h-3" /> Open</a>
                  <button onClick={() => setOpenFile(f)} className="text-xs text-[#0f5c3d] hover:underline inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Discuss</button>
                  {(f.versions_count || 1) > 1 && (
                    <button onClick={() => setOpenVersions(f)} data-testid="file-versions-btn"
                      className="text-xs text-amber-700 hover:underline inline-flex items-center gap-1">
                      <HistoryIcon className="w-3 h-3" /> {f.versions_count} versions
                    </button>
                  )}
                  <button onClick={() => remove(f)} className="text-xs text-red-600 hover:underline">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {previewFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={() => setPreviewFile(null)} data-testid="file-preview-modal">
          <div className="relative w-full max-w-5xl max-h-[95vh]" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPreviewFile(null)} className="absolute -top-10 right-0 text-white p-2 hover:bg-white/10 rounded"><X className="w-5 h-5" /></button>
            {isImg(previewFile) ? (
              <img src={resolveFileUrl(previewFile.url)} alt={previewFile.filename} className="w-full max-h-[95vh] object-contain" />
            ) : (
              <iframe src={resolveFileUrl(previewFile.url)} title={previewFile.filename} className="w-full h-[85vh] bg-white rounded-lg" />
            )}
            <div className="mt-2 text-white/80 text-xs text-center">{previewFile.filename} · v{previewFile.version || 1}</div>
          </div>
        </div>
      )}

      {openVersions && (
        <VersionHistoryModal file={openVersions} onClose={() => setOpenVersions(null)} />
      )}

      {openFile && <FileDiscussion file={openFile} onClose={() => setOpenFile(null)} userLookup={userLookup} currentUserId={currentUserId} />}
    </div>
  );
};

const VersionHistoryModal = ({ file, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    listFileVersions(file.id).then((r) => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [file.id]);
  return (
    <Modal onClose={onClose}>
      <ModalHeader title={`Version history — ${file.filename}`} onClose={onClose} />
      <div className="p-6 space-y-2">
        {loading ? <div className="text-center py-4"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          : items.map((v) => (
            <div key={v.id} data-testid="file-version-row"
              className={`flex items-center gap-3 p-2 rounded ${v.is_latest ? 'bg-emerald-50 border border-emerald-200' : 'bg-[#f7fbf8]'}`}>
              <div className="w-10 h-10 rounded bg-white flex items-center justify-center text-xs font-bold text-[#0f5c3d]">v{v.version || 1}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#0b1a2e]">
                  {v.filename} {v.is_latest && <span className="ml-1 text-[10px] bg-emerald-600 text-white px-1.5 py-0.5 rounded">Latest</span>}
                </div>
                <div className="text-[11px] text-[#0b1a2e]/60">{v.uploaded_by_name || 'user'} · {fmt(v.uploaded_at)}</div>
              </div>
              <a href={resolveFileUrl(v.url)} target="_blank" rel="noreferrer" className="text-xs text-[#0f5c3d] hover:underline inline-flex items-center gap-1"><Download className="w-3 h-3" /> Open</a>
            </div>
          ))}
      </div>
    </Modal>
  );
};

const FileDiscussion = ({ file, onClose, userLookup, currentUserId }) => {
  const [items, setItems] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await listFileComments(file.id); setItems(r.data || []); } catch (err) { console.error('listFileComments failed', err); } finally { setLoading(false); }
  }, [file.id]);
  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await createFileComment({ file_id: file.id, body: text });
      setText('');
      load();
    } catch { toast({ title: 'Send failed', variant: 'destructive' }); }
  };

  return (
    <Modal onClose={onClose}>
      <ModalHeader title={`Discussion — ${file.filename}`} onClose={onClose} />
      <div className="p-6">
        <div className="max-h-96 overflow-y-auto space-y-3">
          {loading ? (
            <div className="text-center py-6 text-[#0b1a2e]/50"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-6 text-sm text-[#0b1a2e]/50">No comments yet.</div>
          ) : items.map((c) => (
            <div key={c.id} className="bg-[#f7fbf8] rounded-lg p-3">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-[#0f5c3d]">{userLookup[c.created_by]?.name || 'User'} · {fmt(c.created_at)}</div>
              <div className="mt-1 text-sm text-[#0b1a2e] whitespace-pre-wrap">{c.body}</div>
            </div>
          ))}
        </div>
        <form onSubmit={send} className="mt-4 flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a comment…" data-testid="file-comment-input" className="flex-1 border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
          <button type="submit" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-[#0f5c3d] text-white text-sm"><Send className="w-4 h-4" /></button>
        </form>
      </div>
    </Modal>
  );
};

const DepartmentManager = ({ onClose, depts, onChange }) => {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try { await createDepartment({ name }); setName(''); onChange(); }
    catch (err) { toast({ title: 'Failed', variant: 'destructive', description: err?.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete department "${d.name}"?`)) return;
    await deleteDepartment(d.id);
    onChange();
  };
  return (
    <Modal onClose={onClose}>
      <ModalHeader title="Departments" onClose={onClose} />
      <div className="p-6 space-y-3">
        <form onSubmit={add} className="flex gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Department name" data-testid="new-dept-input" className="flex-1 border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
          <button type="submit" disabled={saving} data-testid="add-dept-btn" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-[#0f5c3d] text-white text-sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
          </button>
        </form>
        {depts.length === 0 ? (
          <div className="text-center text-sm text-[#0b1a2e]/50 py-4">No departments yet.</div>
        ) : depts.map((d) => (
          <div key={d.id} data-testid="dept-row" className="flex items-center justify-between bg-[#f7fbf8] rounded-lg px-3 py-2">
            <div>
              <div className="font-medium text-sm text-[#0b1a2e]">{d.name}</div>
              <div className="text-[11px] text-[#0b1a2e]/50 font-mono">{d.slug}</div>
            </div>
            <button onClick={() => remove(d)} className="p-1.5 rounded-lg text-red-600 hover:bg-red-100"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </Modal>
  );
};

// ---- Reusable modal shell ----
const Modal = ({ children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black/50" onClick={onClose} />
    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">{children}</div>
  </div>
);
const ModalHeader = ({ title, onClose }) => (
  <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
    <div className="text-lg font-semibold text-[#0b1a2e]">{title}</div>
    <button onClick={onClose} className="p-2 hover:bg-black/5 rounded-lg"><X className="w-4 h-4" /></button>
  </div>
);
const ModalFooter = ({ children }) => (
  <div className="px-6 py-4 border-t border-black/5 bg-[#f7fbf8] flex justify-end gap-2">{children}</div>
);
const FormField = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">{label}</span>
    {children}
  </label>
);

export default CRMPage;
